/**
 * Registry of available behavior transformers in the application.
 * Behavior transformers allow plugins and themes to modify or enhance the specific behaviors and interactions.
 * These transformers are invoked at key points to allow customization of the application behavior.
 *
 * USE ONLY lowercase names
 *
 * @constant {ReadonlyArray<string>} BEHAVIOR_TRANSFORMERS - An immutable array of behavior transformer identifiers
 */
// eslint-discourse keep-array-sorted
export const BEHAVIOR_TRANSFORMERS = Object.freeze([
  "admin-user-unsilence",
  "admin-user-unsuspend",
  "apply-local-dates",
  "category-visibility-change",
  "composer-actions-on-select",
  "composer-apply-topic-template",
  "composer-open",
  "composer-position:correct-scroll-position",
  "composer-position:editor-touch-move",
  "create-account",
  "create-topic-button-click",
  "custom-homepage-model",
  "discovery-topic-list-load-more",
  "full-page-search-load-more",
  "post-menu-toggle-like-action",
  "post-stream-error-loading",
  "post-stream-suggested-topics",
  "post-stream-update-from-json",
  "search-menu-clear-recent-searches",
  "topic-controller:finished-editing",
  "topic-list-item-click",
  "topic-update-from-json",
]);

/**
 * Registry of available value transformers in the application.
 * Value transformers allow plugins and themes to modify or replace specific values before they are used by the application.
 * Each transformer represents a specific value or computation that can be customized.
 *
 * USE ONLY lowercase names
 *
 * @constant {ReadonlyArray<string>} VALUE_TRANSFORMERS - An immutable array of value transformer identifiers
 */
// eslint-discourse keep-array-sorted
export const VALUE_TRANSFORMERS = Object.freeze([
  "admin-onboarding-start-posting-options",
  "admin-plugin-icon",
  "admin-reports-show-query-params",
  "admin-theme-card-show-full-controls",
  "bulk-select-in-nav-controls",
  "can-create-topic-button",
  "category-available-views",
  "category-default-colors",
  "category-description-text",
  "category-display-name",
  "category-list-attributes",
  "category-list-request",
  "category-list-subcategories",
  "category-sort-orders",
  "category-subcategories",
  "category-text-color",
  "category-visibility-private-locked",
  "code-login-user-field-values",
  "composer-actions-content",
  "composer-editing-post",
  "composer-editor-quoted-post-avatar-template",
  "composer-editor-reply-placeholder",
  "composer-force-editor-mode",
  "composer-message-components",
  "composer-minimum-post-length",
  "composer-reply-options-user-avatar-template",
  "composer-reply-options-user-link-name",
  "composer-save-button-label",
  "composer-service-cannot-submit-post",
  "composer-toggles-class",
  "composer-user",
  "create-topic-button-class",
  "create-topic-button-draft-menu-class",
  "create-topic-icon",
  "create-topic-label",
  "draft-icon",
  "flag-button-disabled-state",
  "flag-button-dynamic-class",
  "flag-button-render-decision",
  "flag-custom-placeholder",
  "flag-description",
  "flag-formatted-name",
  "full-page-refresh-on-navigation",
  "full-page-search-button-icon",
  "full-page-search-button-label",
  "full-page-search-no-results-enabled",
  "full-page-search-types",
  "group-members-request",
  "hamburger-dropdown-click-outside-exceptions",
  "header-notifications-avatar-size",
  "home-logo-href",
  "home-logo-image-url",
  "home-logo-minimized",
  "invite-simple-mode-topic",
  "latest-topic-list-item-class",
  "like-button-render-decision",
  "mentions-class",
  "more-topics-tabs",
  "move-to-topic-merge-options",
  "move-to-topic-move-options",
  "navigation-bar-dropdown-icon",
  "navigation-bar-dropdown-mode",
  "navigation-items",
  "notifications-tracking-description",
  "parent-category-row-class",
  "parent-category-row-class-mobile",
  "post-article-class",
  "post-avatar-class",
  "post-avatar-size",
  "post-avatar-template",
  "post-can-edit",
  "post-class",
  "post-event-listener",
  "post-flag-available-flags",
  "post-flag-title",
  "post-is-small-action",
  "post-language-selector-priority",
  "post-menu-buttons",
  "post-menu-collapsed",
  "post-menu-like-button-icon",
  "post-meta-data-edits-indicator-label",
  "post-meta-data-infos",
  "post-meta-data-poster-name-suppress-similar-name",
  "post-notice-component",
  "post-show-topic-map",
  "post-small-action-class",
  "post-small-action-custom-component",
  "post-small-action-icon",
  "poster-name-class",
  "poster-name-icons",
  "poster-name-user-title",
  "preferences-save-attributes",
  "quote-params",
  "reviewable-component",
  "route-to-url",
  "search-advanced-icon-enabled",
  "search-menu-advanced-button-enabled",
  "search-menu-full-search-params",
  "search-menu-indexed-results-enabled",
  "search-menu-input-placeholder",
  "search-menu-input-wrapper-classes",
  "search-menu-recent-searches",
  "search-menu-search-context-enabled",
  "search-menu-search-shortcuts-enabled",
  "sidebar-anonymous-default-categories",
  "site-setting-allows-none",
  "small-user-attrs",
  "tag-separator",
  "topic-escaped-excerpt",
  "topic-list-class",
  "topic-list-columns",
  "topic-list-header-sortable-column",
  "topic-list-item-class",
  "topic-list-item-expand-pinned",
  "topic-list-item-mobile-layout",
  "topic-list-item-style",
  "topic-list-topics-from",
  "topic-navigation-render-timeline",
  "topic-reply-count",
  "topic-show-footer-buttons",
  "user-field-components",
  "user-list-display-name",
  "user-list-display-username",
  "user-location",
  "user-menu-notification-item-acting-user-avatar",
  "user-notes-modal-subtitle",
  "user-path",
  "welcome-banner-display-for-route",
]);
